import React from "react";
import { useFetchPublicProjectImagesQuery } from "../../../store/api/projectImagesApi";
import "./BrandsSection.css";

const MATERIALS = [
  {
    id: "Cement" as const,
    name: "Cement",
    icon: "bi-bag-fill",
    accent: "#78716c",
    light: "#fafaf9",
    desc: "High-strength cement for solid structural foundations",
    brands: ["UltraTech", "ACC", "Ambuja"],
    fallbackImage: "/images/bandimg/cement.png",
  },
  {
    id: "Steel" as const,
    name: "Steel Bar",
    icon: "bi-dash-lg",
    accent: "#475569",
    light: "#f8fafc",
    desc: "Quality TMT steel bars for safe, earthquake-resistant framework",
    brands: ["TATA Steel", "JSW", "SAIL", "Jindal", "Kamdhenu"],
    fallbackImage: "/images/bandimg/steelbars.png",
  },
  {
    id: "Paint" as const,
    name: "Paints",
    icon: "bi-brush-fill",
    accent: "#ec4899",
    light: "#fdf2f8",
    desc: "Long-lasting interior and exterior finishes in every shade",
    brands: ["Asian Paints", "Berger", "Nerolac"],
    fallbackImage: "/images/bandimg/paints.png",
  },
  {
    id: "Tiles" as const,
    name: "Tiles",
    icon: "bi-grid-3x3-gap-fill",
    accent: "#7c3aed",
    light: "#f5f3ff",
    desc: "Premium flooring and wall tiles for every space and style",
    brands: ["Kajaria"],
    fallbackImage: "/images/bandimg/tiles.png",
  },
];

const MaterialCard: React.FC<(typeof MATERIALS)[0]> = ({
  id,
  name,
  icon,
  accent,
  light,
  desc,
  brands,
  fallbackImage,
}) => {
  const { data: images = [] } = useFetchPublicProjectImagesQuery(id);
  const photos = images.filter((img) => !!img.imageUrl);

  return (
    <div className='mat-card'>
      {/* ── Photo showcase ───────────────────────── */}
      <div className='mat-photos'>
        {photos.length === 0 ? (
          /* Category photo shown until real project photos are uploaded */
          <img src={fallbackImage} alt={name} className='mat-photo-single' />
        ) : photos.length === 1 ? (
          <img
            src={photos[0].imageUrl}
            alt={name}
            className='mat-photo-single'
          />
        ) : (
          <div className='mat-photo-grid'>
            <img
              src={photos[0].imageUrl}
              alt={`${name} 1`}
              className='mat-photo-main'
            />
            <div className='mat-photo-stack'>
              {photos.slice(1, 3).map((p, i) => (
                <img
                  key={p.id}
                  src={p.imageUrl}
                  alt={`${name} ${i + 2}`}
                  className='mat-photo-thumb'
                />
              ))}
              {photos.length > 3 && (
                <div className='mat-photo-more' style={{ background: accent }}>
                  +{photos.length - 3}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Category badge */}
        <div className='mat-badge' style={{ background: accent }}>
          <i className={`bi ${icon} me-1`} />
          {name}
        </div>
      </div>

      {/* ── Card body ──────────────────────────── */}
      <div className='mat-body' style={{ background: light }}>
        <p className='mat-desc'>{desc}</p>
        <div className='mat-brands'>
          {brands.map((b) => (
            <span
              key={b}
              className='mat-chip'
              style={{ borderColor: accent + "44", color: accent }}
            >
              {b}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const BrandsSection: React.FC = () => (
  <section className='brands-section'>
    <div className='container'>
      <span className='website-section-kicker d-block text-center'>
        Trusted Materials
      </span>
      <h3 className='text-center website-section-title'>
        Quality Materials We Source
      </h3>
      <p className='text-center website-section-subtitle mb-5'>
        Every project uses quality-certified materials from trusted brands —
        giving your space the finish and structural strength it deserves.
      </p>

      <div className='mat-grid'>
        {MATERIALS.map((mat) => (
          <MaterialCard key={mat.id} {...mat} />
        ))}
      </div>
    </div>
  </section>
);

export default BrandsSection;
